// src/pages/reports/components/PMDrawerAccordion.jsx
import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Badge,
  VStack,
  HStack,
} from "@chakra-ui/react";
import {
  collectionGroup,
  getDocs,
  query,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";

// format currency
const fmtGBP = (n) => {
  const num = Number(n || 0);
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(num);
};

// safe field picker
const safe = (v, fallback = "—") =>
  v === undefined || v === null || v === "" ? fallback : v;

// pick first non-empty string helper
const pickFirst = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== "-" && s !== "—") return s;
    if (s === "0") return s;
  }
  return "";
};

// style detection specifically for Glass Doors
function glassDoorStyle(spec = {}, line = {}) {
  return pickFirst(
    spec.subcategory,
    spec.style,
    spec.doorStyle,
    spec.system,
    spec.systemType,
    spec.glassDoorStyle,
    spec.openingStyle,
    spec.type,
    spec.variant,
    spec.model,
    line.style
  );
}

// format extras for basket (handles array of strings OR [{name, qty}])
function formatExtrasForBasket(spec = {}) {
  const ex = spec.extras;
  if (!ex) return "-";
  if (typeof ex === "string") return ex || "-";
  if (Array.isArray(ex)) {
    const parts = ex
      .map((e) => {
        if (typeof e === "string") return e;
        if (e && typeof e.name === "string") {
          const q = Number(e.qty);
          return q && q > 1 ? `${e.name} ×${q}` : e.name;
        }
        return "";
      })
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "-";
  }
  return "-";
}

// item label used in basket for Windows rows (best-effort mirror)
function itemLabelForRow(line = {}) {
  // Prefer explicit name; otherwise derive a concise label
  const spec = line.spec || {};
  return (
    pickFirst(
      line.name,
      spec.name,
      `${pickFirst(spec.style, spec.subcategory, spec.doorStyle, spec.type) || ""} ${pickFirst(spec.group, spec.variant) || ""}`.trim()
    ) || "Item"
  );
}

// WINDOWS: append " + Toughened" when spec.toughened === true
// or glazing string already contains "toughened" (case-insensitive).
function glazingWithToughened(spec = {}) {
  const glazing = safe(spec.glazing);
  const toughened =
    spec.toughened === true ||
    (typeof glazing === "string" && glazing.toLowerCase().includes("toughened"));
  return toughened ? `${glazing} + Toughened` : glazing;
}

const CATEGORY_ORDER = ["Windows", "Doors", "Glass Doors", "Works & Extras"];

export default function PMDrawerAccordion({ orgId, lead }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId || !lead?.id) return;

    const load = async () => {
      setLoading(true);
      try {
        const q = query(
          collectionGroup(db, "quotes"),
          where("orgId", "==", orgId),
          where("leadId", "==", lead.id),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setQuotes(rows);
      } catch (e) {
        console.error("Error loading quotes:", e);
        setQuotes([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [orgId, lead?.id]);

  if (loading) {
    return (
      <Box mt={2}>
        <Spinner size="sm" /> <Text as="span">Loading quotes…</Text>
      </Box>
    );
  }

  if (!quotes.length) {
    return (
      <Box mt={2}>
        <Text color="gray.600">No saved quotes for this lead.</Text>
      </Box>
    );
  }

  // ---------- headers by category (mirror Basket) ----------
  function renderHeaderByCategory(category) {
    if (category === "Doors") {
      return (
        <Tr>
          <Th>Location</Th>
          <Th>Type</Th>
          <Th>Variant</Th>
          <Th isNumeric>Width</Th>
          <Th isNumeric>Height</Th>
          <Th>Colour</Th>
          <Th>Frame</Th>
          <Th>Extras</Th>
          <Th>Accessories</Th>
          <Th isNumeric>Qty</Th>
          <Th isNumeric>Unit</Th>
          <Th isNumeric>Total</Th>
        </Tr>
      );
    }
    if (category === "Glass Doors") {
      return (
        <Tr>
          <Th>Location</Th>
          <Th>Style</Th>
          <Th isNumeric>Width</Th>
          <Th isNumeric>Height</Th>
          <Th>Colour</Th>
          <Th>Glazing</Th>
          <Th>Type</Th>
          <Th isNumeric>Qty</Th>
          <Th isNumeric>Unit</Th>
          <Th isNumeric>Total</Th>
        </Tr>
      );
    }
    if (category === "Works & Extras") {
      return (
        <Tr>
          <Th>Item</Th>
          <Th>Unit</Th>
          <Th isNumeric>Price/Unit</Th>
          <Th isNumeric>Qty</Th>
          <Th isNumeric>Total</Th>
        </Tr>
      );
    }
    // Windows
    return (
      <Tr>
        <Th>Location</Th>
        <Th>Item</Th>
        <Th isNumeric>Width</Th>
        <Th isNumeric>Height</Th>
        <Th>Style</Th>
        <Th>Openers</Th>
        <Th>FixedLights</Th>
        <Th>Glazing</Th>
        <Th>Finish</Th>
        <Th>Colour</Th>
        <Th>Material</Th>
        <Th>Shape</Th>
        <Th>Extras</Th>
        <Th isNumeric>Qty</Th>
        <Th isNumeric>Unit</Th>
        <Th isNumeric>Total</Th>
      </Tr>
    );
  }

  // ---------- row cells by category (mirror Basket) ----------
  function renderRowCellsByCategory(line, idx) {
    const category = line?.category;
    const spec = line?.spec || {};
    const qty = line?.qty ?? spec.qty ?? 1;
    const unitPrice = line?.unitPrice ?? 0;
    const shownTotal = (line?.lineTotal ?? (qty * unitPrice)) || 0;

    if (category === "Doors") {
      return (
        <Tr key={idx}>
          <Td>{safe(spec.location)}</Td>
          <Td>{safe(spec.type)}</Td>
          <Td>{safe(spec.variant)}</Td>
          <Td isNumeric>{safe(spec.width_mm ?? spec.widthMM)}</Td>
          <Td isNumeric>{safe(spec.height_mm ?? spec.heightMM)}</Td>
          <Td>{safe(spec.colour ?? spec.color)}</Td>
          <Td>{safe(spec.frame)}</Td>
          <Td>{formatExtrasForBasket(spec)}</Td>
          <Td>{safe(spec.accessories)}</Td>
          <Td isNumeric>{qty}</Td>
          <Td isNumeric>{fmtGBP(unitPrice)}</Td>
          <Td isNumeric>{fmtGBP(shownTotal)}</Td>
        </Tr>
      );
    }

    if (category === "Glass Doors") {
      const styleLabel = glassDoorStyle(spec, line) || "-";
      const typeVal = pickFirst(spec.type, spec.material, spec.systemType, "uPVC");
      return (
        <Tr key={idx}>
          <Td>{safe(spec.location)}</Td>
          <Td>{styleLabel}</Td>
          <Td isNumeric>{safe(spec.width_mm ?? spec.widthMM)}</Td>
          <Td isNumeric>{safe(spec.height_mm ?? spec.heightMM)}</Td>
          <Td>{safe(spec.colour ?? spec.color)}</Td>
          <Td>{safe(spec.glazing)}</Td>
          <Td>{safe(typeVal)}</Td>
          <Td isNumeric>{qty}</Td>
          <Td isNumeric>{fmtGBP(unitPrice)}</Td>
          <Td isNumeric>{fmtGBP(shownTotal)}</Td>
        </Tr>
      );
    }

    if (category === "Works & Extras") {
      // Unit type & qty derivation
      const hasArea = spec.area_m2 != null && spec.area_m2 !== "";
      const hasLen = spec.length_m != null && spec.length_m !== "";
      const unitType = hasArea ? "m2" : hasLen ? "m" : (spec.unit || "item");
      const displayQty =
        (hasArea ? spec.area_m2 : hasLen ? spec.length_m : spec.qty) ??
        qty ??
        1;

      return (
        <Tr key={idx}>
          <Td>{safe(spec.name || line.name || "Extra")}</Td>
          <Td>{unitType}</Td>
          <Td isNumeric>{fmtGBP(unitPrice)}</Td>
          <Td isNumeric>{safe(displayQty)}</Td>
          <Td isNumeric>{fmtGBP(shownTotal)}</Td>
        </Tr>
      );
    }

    // Windows (default)
    const styleLabel = spec.style || "-";
    const extrasLabel = formatExtrasForBasket(spec);

    return (
      <Tr key={idx}>
        <Td>{safe(spec.location)}</Td>
        <Td>{itemLabelForRow(line)}</Td>
        <Td isNumeric>{safe(spec.width_mm ?? spec.widthMM)}</Td>
        <Td isNumeric>{safe(spec.height_mm ?? spec.heightMM)}</Td>
        <Td>{safe(styleLabel)}</Td>
        <Td>{safe(spec.openers)}</Td>
        <Td>{safe(spec.fixedLights)}</Td>
        <Td>{glazingWithToughened(spec)}</Td>
        <Td>{String(spec.finish || "").replace(/\s*Finish\s*$/i, "") || "-"}</Td>
        <Td>{safe(spec.colour ?? spec.color)}</Td>
        <Td>{safe(spec.material)}</Td>
        <Td>{safe(spec.shape)}</Td>
        <Td>{extrasLabel}</Td>
        <Td isNumeric>{qty}</Td>
        <Td isNumeric>{fmtGBP(unitPrice)}</Td>
        <Td isNumeric>{fmtGBP(shownTotal)}</Td>
      </Tr>
    );
  }

  return (
    <Accordion allowMultiple mt={2}>
      {quotes.map((q) => {
        // IMPORTANT: keep raw items to mirror Basket layout/logic
        const items = Array.isArray(q.items) ? q.items : [];
        const created =
          q.createdAt?.toDate?.().toLocaleString?.() ||
          q.createdAt ||
          "Unknown date";
        const total = fmtGBP(q.total);

        // Group items by category to render them in separate tables
        const grouped = items.reduce((acc, item) => {
          const cat = item?.category || "Other";
          (acc[cat] = acc[cat] || []).push(item);
          return acc;
        }, {});

        return (
          <AccordionItem
            key={q.id}
            border="1px solid"
            borderColor="gray.200"
            rounded="md"
            mb={2}
          >
            <h2>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="semibold">
                    Quote #{q.id.slice(-6)} • {created}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Sales Rep: {q.salespersonName || q.spName || "—"}
                  </Text>
                </Box>
                <Badge colorScheme="purple" mr={3}>
                  {total}
                </Badge>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={4}>
              {items.length === 0 ? (
                <Text color="gray.600">No line items.</Text>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {CATEGORY_ORDER.map((category) => {
                    const categoryItems = grouped[category] || [];
                    if (categoryItems.length === 0) return null;

                    return (
                      <Box
                        key={category}
                        p={2}
                        border="1px solid #edf2f7"
                        borderRadius="md"
                      >
                        <Text fontWeight="semibold" mb={2}>
                          {category}
                        </Text>
                        <Box
                          maxW="100%"
                          overflowX="auto"
                          whiteSpace="nowrap"
                          sx={{
                            WebkitOverflowScrolling: "touch",
                            scrollbarWidth: "thin",
                            msOverflowStyle: "auto",
                            scrollbarGutter: "stable both-edges",
                          }}
                        >
                          <Table
                            size="sm"
                            variant="striped"
                            style={{ width: "max-content" }}
                          >
                            <Thead>{renderHeaderByCategory(category)}</Thead>
                            <Tbody>
                              {categoryItems.map((line, idx) =>
                                renderRowCellsByCategory(line, idx)
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    );
                  })}
                  <HStack justify="flex-end" px={2} pt={2}>
                    <Text fontWeight="bold">Grand Total: {total}</Text>
                  </HStack>
                </VStack>
              )}
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
